import { computed, ref } from 'vue';
import { api } from '../services/api';
import { localSync } from '../services/LocalSyncService';
import type { WebUser } from '../types';

const currentUser = ref<WebUser | null>(api.getSavedUser());
const isAuthChecking = ref<boolean>(false);

export function useAuth() {
  const isLoggedIn = computed(() => Boolean(currentUser.value));

  const setUser = (user: WebUser, token?: string) => {
    const normalized: WebUser = {
      ...user,
      user_id: String(user.user_id ?? (user as any).id ?? 'anonymous'),
    };
    currentUser.value = normalized;
    api.saveUser(normalized);
    if (token) {
      api.setToken(token);
    }
  };

  const logout = () => {
    localSync.stop();
    currentUser.value = null;
    api.clearToken(true);
  };

  const tryRestoreOfflineSession = async (fallbackUser: WebUser | null): Promise<boolean> => {
    const discovered = await localSync.discoverLocalUserWithWords(fallbackUser?.user_id);
    if (discovered && discovered.wordsCount > 0) {
      const restoredUser: WebUser = fallbackUser || {
        user_id: discovered.userId,
        first_name: 'Offline Learner',
      };
      currentUser.value = restoredUser;
      api.saveUser(restoredUser);
      return true;
    }
    if (fallbackUser && api.getToken()) {
      currentUser.value = fallbackUser;
      return true;
    }
    return false;
  };

  const checkAuth = async (): Promise<boolean> => {
    const token = api.getToken();
    const savedUser = api.getSavedUser() || api.getLastOfflineUser();
    const browserOffline = typeof navigator !== 'undefined' && !navigator.onLine;

    // 1. Offline-first: when browser has no internet, allow immediate study if saved session or local IndexedDB words exist
    if (browserOffline) {
      localSync.state.connection = 'offline';
      const restored = await tryRestoreOfflineSession(savedUser);
      if (restored) {
        return true;
      }
      currentUser.value = null;
      return false;
    }

    // 2. When online without a token: check if no token exists
    if (!token) {
      currentUser.value = null;
      return false;
    }

    if (savedUser) {
      currentUser.value = savedUser;
    }

    isAuthChecking.value = true;
    try {
      const state = await api.getSessionState({ timeoutMs: 2500 });
      setUser(state.user);
      return true;
    } catch (e: any) {
      console.warn('Auth check warning:', e);
      if (e?.status === 401) {
        // Even if token expired, if local words exist and device is offline, allow offline study
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          localSync.state.connection = 'offline';
          if (await tryRestoreOfflineSession(savedUser)) {
            return true;
          }
        }
        logout();
        return false;
      }

      // Network unreachable / timeout ("lie-fi") while user has valid cached session or local DB words -> stay logged in for offline study
      localSync.state.connection =
        typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'unreachable';
      if (await tryRestoreOfflineSession(savedUser)) {
        return true;
      }
      logout();
      return false;
    } finally {
      isAuthChecking.value = false;
    }
  };

  return {
    currentUser,
    isLoggedIn,
    isAuthChecking,
    setUser,
    logout,
    checkAuth,
  };
}
